import { useEffect, useRef, useState } from "react";
import { DotField } from "../dotfield/DotField";
import { sampleShape } from "./shapes";
import "./ParticleField.css";

// ====================================================================
// ParticleField — the hero's centrepiece. Thousands of points in the
// logo blues hold one shape at a time (the NSD mark, a site, an app, a
// neural net, a #1 ranking, a roadmap) as a thin 3D slab that slowly
// sways in perspective, inside a cloud of drifting dust. Changing shape
// is one full turn: the swarm spins, swirls and lifts toward the viewer,
// then lands as the next shape. It runs on its own; the pointer does
// nothing to it. Everything after the morph targets runs on the GPU.
// ====================================================================

const MORPH_MS = 2600;
const INTRO_MS = 3200;

const VERT = `
attribute vec2 aFrom;
attribute vec2 aTo;
attribute vec4 aRand;
attribute float aKind;
uniform vec2 uRes;
uniform float uDpr;
uniform float uTime;
uniform float uMorph;
uniform float uFade;
uniform vec4 uBox;
varying vec3 vColor;
varying float vAlpha;

float ease(float t) {
	return t < 0.5 ? 4.0 * t * t * t : 1.0 - pow(-2.0 * t + 2.0, 3.0) / 2.0;
}

void main() {
	float amb = aKind;
	vec2 c = uBox.xy + uBox.zw * 0.5;

	// Each particle leaves a little after the last, so the swarm streams.
	float p = clamp((uMorph - aRand.x * 0.3) / 0.7, 0.0, 1.0);
	float e = ease(p);
	float flight = sin(e * 3.14159);

	vec2 q = mix(aFrom, aTo, e) - c;
	float sw = flight * (0.7 + aRand.y * 1.8);
	q = vec2(q.x * cos(sw) - q.y * sin(sw), q.x * sin(sw) + q.y * cos(sw));
	float z = (aRand.w - 0.5) * 44.0 + flight * (aRand.z - 0.5) * 560.0;

	// The dust is a deep cloud around the shape, so it parallaxes as it turns.
	if (amb > 0.5) {
		q = aTo - c;
		z = (aRand.w - 0.5) * 900.0;
	}
	q += vec2(sin(uTime * 0.5 + aRand.z * 40.0), cos(uTime * 0.4 + aRand.x * 50.0)) * mix(0.8, 7.0, amb);

	// Slow sway, plus one full turn while morphing into the next shape.
	float yaw = sin(uTime * 0.21) * 0.5 + ease(uMorph) * 6.28318 * (1.0 - amb);
	float pitch = sin(uTime * 0.16 + 1.3) * 0.16;
	float x1 = q.x * cos(yaw) + z * sin(yaw);
	float z1 = -q.x * sin(yaw) + z * cos(yaw);
	float y1 = q.y * cos(pitch) - z1 * sin(pitch);
	float z2 = min(q.y * sin(pitch) + z1 * cos(pitch), 800.0);
	float persp = 1100.0 / (1100.0 - z2);
	vec2 pos = c + vec2(x1, y1) * persp;

	vec2 clip = (pos / uRes) * 2.0 - 1.0;
	gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);

	float size = mix(2.0 + aRand.y * 2.6, 0.8 + aRand.y * 1.7, amb);
	gl_PointSize = size * persp * uDpr;

	float ct = clamp((aTo.x - uBox.x) / uBox.z, 0.0, 1.0) * 0.75 + aRand.w * 0.25;
	vec3 deep = vec3(0.20, 0.45, 0.84);
	vec3 mid = vec3(0.41, 0.65, 0.83);
	vec3 light = vec3(0.89, 0.94, 0.97);
	vec3 col = ct < 0.5 ? mix(deep, mid, ct * 2.0) : mix(mid, light, ct * 2.0 - 1.0);
	col = mix(col, mix(deep, mid, aRand.w), amb);
	// Particles in flight burn a little hotter.
	vColor = mix(col, vec3(1.0), flight * 0.45 * (1.0 - amb));

	float depth = clamp(0.6 + z2 / 600.0, 0.25, 1.25);
	float tw = 0.78 + 0.22 * sin(uTime * 2.0 + aRand.z * 30.0);
	vAlpha = mix(0.85, 0.38, amb) * tw * depth * (1.0 - uFade * 0.9);
}
`;

const FRAG = `
precision mediump float;
varying vec3 vColor;
varying float vAlpha;
void main() {
	float d = length(gl_PointCoord - 0.5);
	float a = smoothstep(0.5, 0.05, d) * vAlpha;
	gl_FragColor = vec4(vColor * a, a);
}
`;

function compile(gl, type, src) {
	const s = gl.createShader(type);
	gl.shaderSource(s, src);
	gl.compileShader(s);
	if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
	return s;
}

function createEngine(canvas, getAnchor, reduced, onIntroDone) {
	const gl = canvas.getContext("webgl", { antialias: false, alpha: true, premultipliedAlpha: true });
	if (!gl) return null;

	const prog = gl.createProgram();
	gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
	gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
	gl.linkProgram(prog);
	if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
	gl.useProgram(prog);
	gl.enable(gl.BLEND);
	gl.blendFunc(gl.ONE, gl.ONE);

	const loc = (n) => gl.getUniformLocation(prog, n);
	const U = {
		res: loc("uRes"),
		dpr: loc("uDpr"),
		time: loc("uTime"),
		morph: loc("uMorph"),
		fade: loc("uFade"),
		box: loc("uBox"),
	};

	const section = canvas.closest("section") || canvas.parentElement;
	const state = {
		w: 0,
		h: 0,
		dpr: 1,
		total: 0,
		shapeCount: 0,
		from: null,
		to: null,
		rand: null,
		box: { x: 0, y: 0, w: 1, h: 1 },
		shape: "logo",
		pending: null,
		cache: new Map(),
		morphStart: -1e9,
		morphMs: MORPH_MS,
		fade: 0,
		visible: true,
		raf: 0,
		t0: performance.now(),
	};
	const buffers = {};

	const attr = (name, size, data, usage) => {
		const b = buffers[name] || gl.createBuffer();
		buffers[name] = b;
		gl.bindBuffer(gl.ARRAY_BUFFER, b);
		gl.bufferData(gl.ARRAY_BUFFER, data, usage);
		const l = gl.getAttribLocation(prog, name);
		gl.enableVertexAttribArray(l);
		gl.vertexAttribPointer(l, size, gl.FLOAT, false, 0, 0);
	};

	const morphT = () => Math.min(1, Math.max(0, (performance.now() - state.morphStart) / state.morphMs));

	const targets = (name) => {
		if (!state.cache.has(name)) {
			const { x, y, w, h } = state.box;
			const pts = sampleShape(name, w, h, state.shapeCount);
			for (let i = 0; i < pts.length; i += 2) {
				pts[i] += x;
				pts[i + 1] += y;
			}
			state.cache.set(name, pts);
		}
		return state.cache.get(name);
	};

	const upload = () => {
		attr("aFrom", 2, state.from, gl.DYNAMIC_DRAW);
		attr("aTo", 2, state.to, gl.DYNAMIC_DRAW);
	};

	// Morphs always start from a settled shape; a request that arrives
	// mid-turn waits for the turn to finish.
	const morphTo = (name) => {
		state.from.set(state.to.subarray(0, state.shapeCount * 2));
		state.shape = name;
		state.to.set(targets(name));
		upload();
		state.morphMs = MORPH_MS;
		state.morphStart = performance.now();
	};

	const layout = (intro) => {
		const r = section.getBoundingClientRect();
		state.w = r.width;
		state.h = r.height;
		state.dpr = Math.min(window.devicePixelRatio || 1, 2);
		canvas.width = Math.round(r.width * state.dpr);
		canvas.height = Math.round(r.height * state.dpr);
		gl.viewport(0, 0, canvas.width, canvas.height);

		const a = getAnchor();
		if (a) {
			const ar = a.getBoundingClientRect();
			state.box = { x: ar.left - r.left, y: ar.top - r.top, w: ar.width, h: ar.height };
		} else {
			state.box = { x: r.width * 0.15, y: r.height * 0.1, w: r.width * 0.7, h: r.height * 0.4 };
		}

		const total = Math.round(Math.min(12000, Math.max(5600, (r.width * r.height) / 105)));
		const shapeCount = Math.round(total * 0.8);
		const sizeChanged = total !== state.total;
		state.total = total;
		state.shapeCount = shapeCount;
		state.cache.clear();

		if (sizeChanged || !state.rand) {
			state.rand = new Float32Array(total * 4);
			for (let i = 0; i < state.rand.length; i++) state.rand[i] = Math.random();
			const kind = new Float32Array(total);
			kind.fill(1, shapeCount);
			attr("aRand", 4, state.rand, gl.STATIC_DRAW);
			attr("aKind", 1, kind, gl.STATIC_DRAW);
		}

		state.from = new Float32Array(total * 2);
		state.to = new Float32Array(total * 2);
		const tgt = targets(state.shape);
		state.to.set(tgt);
		const cx = state.box.x + state.box.w / 2;
		const cy = state.box.y + state.box.h / 2;
		const reach = Math.hypot(r.width, r.height);
		for (let i = 0; i < shapeCount; i++) {
			if (intro) {
				// Start as dust across the whole screen, then spin into the logo.
				const ang = Math.random() * Math.PI * 2;
				const dist = reach * 0.5 * Math.sqrt(Math.random());
				state.from[i * 2] = cx + Math.cos(ang) * dist * 1.2;
				state.from[i * 2 + 1] = cy + Math.sin(ang) * dist * 0.8;
			} else {
				state.from[i * 2] = tgt[i * 2];
				state.from[i * 2 + 1] = tgt[i * 2 + 1];
			}
		}
		for (let i = shapeCount; i < total; i++) {
			const x = Math.random() * r.width;
			const y = Math.random() * r.height;
			state.from[i * 2] = state.to[i * 2] = x;
			state.from[i * 2 + 1] = state.to[i * 2 + 1] = y;
		}
		upload();
		if (intro && !reduced) {
			state.morphMs = INTRO_MS;
			state.morphStart = performance.now();
		} else {
			state.morphStart = -1e9;
		}
	};

	const draw = () => {
		if (state.pending && morphT() >= 1) {
			const next = state.pending;
			state.pending = null;
			if (next !== state.shape) morphTo(next);
		}
		gl.uniform2f(U.res, state.w, state.h);
		gl.uniform1f(U.dpr, state.dpr);
		gl.uniform1f(U.time, reduced ? 0 : (performance.now() - state.t0) / 1000);
		gl.uniform1f(U.morph, reduced ? 1 : morphT());
		gl.uniform1f(U.fade, state.fade);
		gl.uniform4f(U.box, state.box.x, state.box.y, state.box.w, state.box.h);
		gl.clearColor(0, 0, 0, 0);
		gl.clear(gl.COLOR_BUFFER_BIT);
		gl.drawArrays(gl.POINTS, 0, state.total);
	};

	const loop = () => {
		state.raf = 0;
		if (!state.visible || document.hidden) return;
		draw();
		state.raf = requestAnimationFrame(loop);
	};
	const start = () => {
		if (reduced) {
			draw();
			return;
		}
		if (!state.raf) state.raf = requestAnimationFrame(loop);
	};

	const onScroll = () => {
		state.fade = Math.min(1, Math.max(0, window.scrollY / (state.h * 0.85 || 1)));
		if (reduced) draw();
	};
	window.addEventListener("scroll", onScroll, { passive: true });

	const io = new IntersectionObserver(([entry]) => {
		state.visible = entry.isIntersecting;
		if (state.visible) start();
	});
	io.observe(section);
	const onVis = () => !document.hidden && start();
	document.addEventListener("visibilitychange", onVis);

	const introTimers = [];
	let lastW = 0;
	let lastH = 0;
	let resizeTimer = 0;
	const ro = new ResizeObserver(([entry]) => {
		const { width, height } = entry.contentRect;
		// Phone toolbars nudge the height while scrolling; ignore small changes.
		if (Math.abs(width - lastW) < 1 && Math.abs(height - lastH) < 120) return;
		const first = lastW === 0;
		lastW = width;
		lastH = height;
		if (first) return;
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(() => {
			if (state.pending) {
				state.shape = state.pending;
				state.pending = null;
			}
			layout(false);
			start();
		}, 150);
	});

	return {
		init() {
			const r = section.getBoundingClientRect();
			lastW = r.width;
			lastH = r.height;
			layout(true);
			ro.observe(section);
			onScroll();
			start();
			if (reduced) {
				onIntroDone();
				return;
			}
			introTimers.push(setTimeout(onIntroDone, INTRO_MS + 1400));
		},
		setShape(name) {
			if (reduced) {
				if (name === state.shape) return;
				state.shape = name;
				state.to.set(targets(name));
				state.from.set(state.to.subarray(0, state.shapeCount * 2));
				upload();
				draw();
				return;
			}
			if (morphT() < 1) {
				state.pending = name;
				return;
			}
			state.pending = null;
			if (name !== state.shape) morphTo(name);
			start();
		},
		destroy() {
			cancelAnimationFrame(state.raf);
			introTimers.forEach(clearTimeout);
			clearTimeout(resizeTimer);
			ro.disconnect();
			io.disconnect();
			window.removeEventListener("scroll", onScroll);
			document.removeEventListener("visibilitychange", onVis);
			gl.getExtension("WEBGL_lose_context")?.loseContext();
		},
	};
}

/**
 * Props: `shape` (a key of SHAPES), `anchorRef` (element whose box the
 * shapes fill) and `onIntroDone` (called once the logo has formed).
 */
export function ParticleField({ shape, anchorRef, onIntroDone }) {
	const canvasRef = useRef(null);
	const engineRef = useRef(null);
	const [failed, setFailed] = useState(false);
	const introRef = useRef(onIntroDone);
	introRef.current = onIntroDone;
	const shapeRef = useRef(shape);
	shapeRef.current = shape;

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return undefined;
		const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const introDone = () => introRef.current?.();
		// The #1 in the SEO shape uses the display face; make sure it's loading.
		document.fonts?.load?.("800 100px 'Bricolage Grotesque'").catch(() => null);

		let engine = null;
		try {
			engine = createEngine(canvas, () => anchorRef?.current, reduced, introDone);
		} catch (err) {
			engine = null;
		}
		if (!engine) {
			setFailed(true);
			introDone();
			return undefined;
		}
		engineRef.current = engine;
		engine.init();
		engine.setShape(shapeRef.current);

		return () => {
			engine.destroy();
			engineRef.current = null;
		};
	}, [anchorRef]);

	useEffect(() => {
		engineRef.current?.setShape(shape);
	}, [shape]);

	if (failed) return <DotField />;
	return <canvas ref={canvasRef} className="particleField" aria-hidden="true" />;
}
